/**
 * GPT-Powered Keyword Expansion Service
 * 
 * Dynamically expands crisis keywords using OpenAI GPT for improved media relevance matching.
 * Generates context-aware synonyms, journalistic phrases, and related terminology.
 */

class GPTKeywordExpansionService {
  constructor() {
    this.name = 'GPT Keyword Expansion Service';
    this.apiEndpoint = 'https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/generateExecutiveSummary';
    
    console.log('🤖 GPT Keyword Expansion Service initialized');
  }

  /**
   * Expand crisis keywords using GPT
   * @param {Object} crisisData - Crisis incident data
   * @param {Object} orgData - Organization data
   * @param {Array} liveUpdates - Live chat messages/updates
   * @returns {Promise<Array>} - Expanded keyword array
   */
  async expandKeywords(crisisData, orgData, liveUpdates = []) {
    try {
      console.log('🤖 Starting GPT keyword expansion...');
      
      // Build dynamic prompt based on crisis context
      const prompt = this.buildKeywordExpansionPrompt(crisisData, orgData, liveUpdates);
      
      // Call GPT service
      const response = await this.callGPTService(prompt);
      
      // Parse and validate keywords
      const expandedKeywords = this.parseKeywordsFromResponse(response);
      
      console.log(`✅ GPT expanded ${expandedKeywords.length} keywords from crisis context`);
      return expandedKeywords;
      
    } catch (error) {
      console.error('❌ GPT keyword expansion failed:', error);
      
      // Fallback to basic extraction if GPT fails
      return this.fallbackKeywordExtraction(crisisData, orgData, liveUpdates);
    }
  }

  /**
   * Build dynamic GPT prompt based on crisis context
   */
  buildKeywordExpansionPrompt(crisisData, orgData, liveUpdates) {
    // Extract basic crisis info
    const crisisTitle = crisisData.title || crisisData.summary || '';
    const crisisType = crisisData.crisisType || '';
    const crisisDescription = crisisData.description || '';
    const orgName = orgData.name || orgData.companyName || '';
    const location = crisisData.location || '';
    
    // Extract relevant text from live updates
    const recentUpdates = liveUpdates
      .slice(-10) // Last 10 updates
      .map(update => update.message || update.text || '')
      .filter(text => text.length > 5)
      .join(' ');

    const contextText = `${crisisTitle} ${crisisDescription} ${recentUpdates}`.trim();
    
    // Build context-aware prompt
    const prompt = `You are an AI assistant for Australian crisis media monitoring. Extract and expand keywords from this incident for news article matching.

INCIDENT CONTEXT:
- Title: "${crisisTitle}"
- Type: "${crisisType}"
- Organization: "${orgName}"
- Location: "${location}"
- Additional Context: "${contextText.substring(0, 500)}"

TASK: Generate a comprehensive keyword list for finding relevant Australian media articles. Include:

1. Direct keywords from the incident
2. Australian media/journalistic synonyms
3. Emergency services terminology
4. Location variants (suburbs → regions → states)
5. Organization references and industry terms
6. Crisis-specific terminology

EXAMPLES:
- "missing staff" → ["missing", "unaccounted for", "staff missing", "employee", "worker", "personnel"]
- "severe weather" → ["severe weather", "storm", "extreme weather", "dangerous conditions", "weather warning"]
- "Cessnock" → ["Cessnock", "Hunter Valley", "NSW", "New South Wales"]
- Crisis type variations and related terms

RULES:
- Focus on Australian context and terminology
- Include emergency services terms (SES, RFS, Police, Ambulance)
- Add location hierarchy (specific → regional → state)
- Include organization industry terms
- Maximum 50 keywords
- Return ONLY a JSON array of strings

Respond with: ["keyword1", "keyword2", "keyword3", ...]`;

    return prompt;
  }

  /**
   * Call GPT service using existing Cloud Function
   */
  async callGPTService(prompt) {
    try {
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          keywordExpansionRequest: true, // Special flag for keyword expansion
          prompt: prompt,
          maxTokens: 500,
          temperature: 0.7
        })
      });

      if (!response.ok) {
        throw new Error(`GPT service error: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle different response formats
      if (data.keywords) {
        return data.keywords;
      } else if (data.summary) {
        // Try to extract keywords from summary response
        return data.summary;
      } else if (data.response) {
        return data.response;
      } else {
        throw new Error('Invalid GPT response format');
      }
      
    } catch (error) {
      console.error('❌ Error calling GPT service:', error);
      throw error;
    }
  }

  /**
   * Parse keywords from GPT response
   */
  parseKeywordsFromResponse(response) {
    try {
      // Try to parse as JSON array first
      if (typeof response === 'string') {
        // Look for JSON array in response
        const jsonMatch = response.match(/\[.*\]/s);
        if (jsonMatch) {
          const keywords = JSON.parse(jsonMatch[0]);
          if (Array.isArray(keywords)) {
            return keywords.filter(k => typeof k === 'string' && k.length > 1);
          }
        }
        
        // Fallback: extract quoted strings
        const quotedStrings = response.match(/"([^"]+)"/g);
        if (quotedStrings) {
          return quotedStrings.map(s => s.replace(/"/g, ''));
        }
        
        // Fallback: split by common delimiters
        return response
          .split(/[,\n\r]+/)
          .map(s => s.trim().replace(/^["'-]+|["'-]+$/g, ''))
          .filter(s => s.length > 1)
          .slice(0, 50); // Limit to 50 keywords
      }
      
      // If response is already an array
      if (Array.isArray(response)) {
        return response.filter(k => typeof k === 'string' && k.length > 1);
      }
      
      throw new Error('Unable to parse keywords from response');
      
    } catch (error) {
      console.error('❌ Error parsing GPT keywords:', error);
      throw error;
    }
  }

  /**
   * Fallback keyword extraction if GPT fails
   */
  fallbackKeywordExtraction(crisisData, orgData, liveUpdates) {
    console.log('🔄 Using fallback keyword extraction...');
    
    const keywords = new Set();
    
    // Extract from crisis title and description
    const crisisText = `${crisisData.title || ''} ${crisisData.description || ''}`.toLowerCase();
    const words = crisisText.match(/\b\w{3,}\b/g) || [];
    words.forEach(word => keywords.add(word));
    
    // Add organization name variants
    if (orgData.name) keywords.add(orgData.name.toLowerCase());
    if (orgData.companyName) keywords.add(orgData.companyName.toLowerCase());
    
    // Add crisis type
    if (crisisData.crisisType) keywords.add(crisisData.crisisType.toLowerCase());
    
    // Add location
    if (crisisData.location) {
      const locationWords = crisisData.location.split(/\s+|,/);
      locationWords.forEach(word => {
        if (word.length > 2) keywords.add(word.toLowerCase());
      });
    }
    
    // Basic emergency terms
    const emergencyTerms = [
      'emergency', 'incident', 'crisis', 'urgent', 'severe', 'critical',
      'missing', 'evacuation', 'weather', 'flood', 'fire', 'accident'
    ];
    emergencyTerms.forEach(term => keywords.add(term));
    
    console.log(`🔄 Fallback extracted ${keywords.size} keywords`);
    return Array.from(keywords).slice(0, 30);
  }
}

export default GPTKeywordExpansionService;